'use client';

import { cn } from '../../lib/utils';
import type { ClinicCapability } from '../../types/clinic-access';

export interface ConversationAccessNoteProps {
  /** True when access is limited to this conversation and does not reach the mailbox. */
  conversationOnly: boolean;
  capabilities: Set<ClinicCapability>;
  className?: string;
}

/**
 * The line that explains why some of this page is missing.
 *
 * A briefed-in collaborator opens a conversation with no inbox around it, no send
 * button, and no archive. Without a word of explanation that reads as a broken page; with
 * one it reads as a considered boundary — which is the whole difference between scoped
 * access being a feature and being a bug report.
 *
 * Deliberately not a warning. Nothing has gone wrong, nobody is being refused anything
 * they asked for, and the calm-design rules leave no room for a coloured alert box: this
 * is a `murmur` line at body size, the same treatment as any other explanatory text.
 */
export function ConversationAccessNote({
  conversationOnly,
  capabilities,
  className,
}: ConversationAccessNoteProps) {
  // Someone with mailbox access has nothing to explain — the page is whole for them.
  if (!conversationOnly) return null;

  const canComment = capabilities.has('comment');
  const canDraft = capabilities.has('draft_reply');
  const canCreateTasks = capabilities.has('create_tasks');

  // Itemised rather than summarised: "You have limited access" tells someone nothing they
  // can act on, while naming the three things they can actually do here does.
  const available = [
    canComment && 'comment',
    canCreateTasks && 'create a task',
    canDraft && 'write a draft reply',
  ].filter((entry): entry is string => typeof entry === 'string');

  return (
    <p className={cn('max-w-measure text-sm leading-relaxed text-murmur', className)}>
      You were given access to this conversation on its own, so the rest of the mailbox stays
      closed.
      {available.length > 0 && <> Here you can {joinWithAnd(available)}.</>}
      {canDraft && !capabilities.has('send_reply') && (
        <> Sending is left to whoever owns the mailbox.</>
      )}
    </p>
  );
}

/** "a", "a and b", "a, b and c" — no Oxford comma, matching the rest of the product's copy. */
function joinWithAnd(entries: string[]): string {
  if (entries.length === 1) return entries[0];
  return `${entries.slice(0, -1).join(', ')} and ${entries[entries.length - 1]}`;
}
